import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "@/lib/api";

const PHASE_COLORS = {
  challenge: "text-[#CFA66A] border-[#CFA66A]/40",
  verification: "text-[#5C7C8A] border-[#5C7C8A]/40",
  funded: "text-[#7A9E80] border-[#7A9E80]/40",
  live: "text-[#7A9E80] border-[#7A9E80]/40",
  demo: "text-[#A39A8E] border-[#2E2A25]",
};

function StatCell({ label, value, valueClass = "text-[#EBE5D9]" }) {
  return (
    <div>
      <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39A8E]">{label}</p>
      <p className={`font-mono text-sm mt-0.5 ${valueClass}`}>{value}</p>
    </div>
  );
}

export function AccountsGrid({ accounts, onLogTrade, refreshKey = 0 }) {
  const [layout, setLayout] = useState("grid");
  const [expanded, setExpanded] = useState(new Set());
  const [propStatus, setPropStatus] = useState({});

  const fetchStatus = async (id) => {
    try {
      const { data } = await api.get(`/auth/prop-status?account_id=${id}`);
      setPropStatus((m) => ({ ...m, [id]: data }));
    } catch (_e) { /* card still shows analytics */ }
  };

  useEffect(() => {
    if (!refreshKey) return;
    setPropStatus({});
    expanded.forEach((id) => fetchStatus(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const toggle = async (id) => {
    const next = new Set(expanded);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
      if (!propStatus[id]) fetchStatus(id);
    }
    setExpanded(next);
  };

  if (!accounts.length) {
    return (
      <div data-testid="accounts-grid-empty" className="mt-4 border border-dashed border-[#2E2A25] bg-[#1A1815] p-4 rounded-sm flex flex-wrap items-center gap-3">
        <p className="font-mono text-xs text-[#A39A8E] flex-1">
          No prop-firm accounts yet — build your portfolio to log trades against accounts.
        </p>
        <Link
          to="/profile"
          data-testid="accounts-grid-create-link"
          className="border border-[#2E2A25] text-[#CFA66A] hover:border-[#CFA66A] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm transition-colors"
        >
          Create in Profile
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="accounts-grid" className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#A39A8E]">
          &mdash; Your accounts <span className="text-[#CFA66A]">({accounts.length})</span>
        </p>
        <div className="flex items-center gap-1">
          {["grid", "list"].map((l) => (
            <button
              key={l}
              data-testid={`accounts-layout-${l}`}
              onClick={() => setLayout(l)}
              className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-sm transition-colors ${
                layout === l
                  ? "bg-[#CFA66A] border-[#CFA66A] text-[#0F0E0C]"
                  : "border-[#2E2A25] text-[#A39A8E] hover:border-[#CFA66A]"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <div className={layout === "grid" ? "grid grid-cols-1 sm:grid-cols-2 gap-3" : "space-y-2"}>
        {accounts.map((a) => {
          const open = expanded.has(a.id);
          const an = a.analytics || {};
          const ps = propStatus[a.id];
          return (
            <div
              key={a.id}
              data-testid={`account-card-${a.id}`}
              onClick={() => toggle(a.id)}
              className={`border rounded-sm cursor-pointer transition-colors ${
                open ? "border-[#CFA66A]/60 bg-[#CFA66A]/5" : "border-[#2E2A25] bg-[#1A1815] hover:border-[#CFA66A]/50"
              } ${open && layout === "grid" ? "sm:col-span-2" : ""}`}
            >
              {/* Preview row */}
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-mono text-sm text-[#EBE5D9] truncate">{a.label}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E] mt-1 flex items-center gap-2 flex-wrap">
                    <span>{formatMoney(a.account_size, a.currency)}</span>
                    <span className={`px-1.5 py-0.5 border rounded-sm ${PHASE_COLORS[a.phase] || PHASE_COLORS.demo}`}>{a.phase}</span>
                    <span className={a.status === "active" ? "text-[#7A9E80]" : a.status === "breached" ? "text-[#B56258]" : "text-[#A39A8E]"}>
                      {a.status}
                    </span>
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`account-log-trade-${a.id}`}
                    onClick={(e) => { e.stopPropagation(); onLogTrade(a.id); }}
                    className="bg-[#CFA66A] text-[#0F0E0C] font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm hover:bg-[#EBE5D9] transition-colors"
                  >
                    Log trade
                  </button>
                  <span className={`font-mono text-xs text-[#A39A8E] transition-transform ${open ? "rotate-180" : ""}`}>▾</span>
                </div>
              </div>

              {/* Expanded details */}
              {open && (
                <div data-testid={`account-details-${a.id}`} className="px-4 pb-4 fade-in" onClick={(e) => e.stopPropagation()}>
                  <div className="pt-3 border-t border-[#2E2A25] grid grid-cols-2 sm:grid-cols-5 gap-3">
                    <StatCell label="Trades" value={an.trade_count ?? 0} />
                    <StatCell label="Win rate" value={`${an.win_rate ?? 0}%`} />
                    <StatCell
                      label="Expectancy"
                      value={formatMoney(an.expectancy ?? 0, a.currency)}
                      valueClass={(an.expectancy ?? 0) > 0 ? "text-[#7A9E80]" : (an.expectancy ?? 0) < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}
                    />
                    <StatCell
                      label="Total P&L"
                      value={formatMoney(an.total_pnl ?? 0, a.currency)}
                      valueClass={(an.total_pnl ?? 0) > 0 ? "text-[#7A9E80]" : (an.total_pnl ?? 0) < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}
                    />
                    <StatCell label="Best pair" value={an.best_pair ? `${an.best_pair.pair}` : "—"} />
                  </div>

                  <div className="mt-3 pt-3 border-t border-[#2E2A25]">
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#A39A8E] mb-2">Prop-rule status</p>
                    {!ps ? (
                      <p className="font-mono text-xs text-[#A39A8E]">Loading…</p>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <StatCell label="Daily limit" value={`-${formatMoney(ps.daily_limit, a.currency)}`} />
                          <StatCell label="Max drawdown" value={`-${formatMoney(ps.max_drawdown, a.currency)}`} />
                          <StatCell
                            label="Today's P&L"
                            value={formatMoney(ps.day_pnl, a.currency)}
                            valueClass={ps.day_pnl > 0 ? "text-[#7A9E80]" : ps.day_pnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}
                          />
                          <StatCell
                            label="Account P&L"
                            value={formatMoney(ps.total_pnl, a.currency)}
                            valueClass={ps.total_pnl > 0 ? "text-[#7A9E80]" : ps.total_pnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}
                          />
                        </div>
                        {ps.breaches?.length > 0 ? (
                          <div className="mt-2 space-y-1">
                            {ps.breaches.map((b, i) => (
                              <p key={i} className="font-mono text-xs text-[#B56258]">⚠ {b.message}</p>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[#7A9E80]">
                            ● Within limits — no breaches
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
